/*
 * pogi-cart — the full /cart page controller (SHOPIFY_HANDOFF §14).
 *
 * The Liquid section (sections/pogi-cart.liquid) renders the whole cart from the
 * live `cart` object — it is the single source of truth for prices, the S&S
 * discount, free-ship/min-order math, and selling-plan options. This element
 * only performs mutations: every change posts to the localized Shopify cart
 * routes rendered on the element WITH the Section Rendering API, then morphs
 * THIS section back in
 * so the freshly-rendered Liquid replaces the old DOM. After each change it
 * settles Shopify's standard cart-lines event so the header count and drawer
 * re-render through Horizon 4.1.3's supported contract (§14.4).
 *
 * Client-only concerns that don't belong in the cart object live here: the
 * cross-sell affinity ordering + exclude-owned (§14.5), the free-ship gap-closer
 * (§14.7), and save-for-later (localStorage).
 * Discount codes use Horizon's native cart-discount-component.
 */
import { morphSection } from '@theme/section-renderer';
import { beginPogiCartUpdate } from '@pogi/cart-contract';
import { addPogiCartLines, assertPogiAjaxCart, withPogiCartMutationLock } from '@pogi/cart-mutation';
import { formatShopifyMoney } from '@pogi/money';
import { cartReflectsLineChange } from '@pogi/cart-recovery';
import {
  savedProperties, savedIdentity, readSavedItems, writeSavedItems,
  prepareSavedLine, prepareSavedMove, reconcileSavedItems, rollbackSavedChange,
} from '@pogi/cart-saved';

const CART_REQUEST_TIMEOUT_MS = 10000;

let I18N = {};
const tpl = (k, fb, vars) => { let s = (I18N && I18N[k]) || fb; if (vars) for (const x in vars) s = s.split('[[' + x + ']]').join(vars[x]); return s; };
const nonEmptyString = (value) => typeof value === 'string' && value.trim() !== '';
const plainRecord = (value) => value && typeof value === 'object' && !Array.isArray(value);


/**
 * Select the cart page's merchant-configured add-ons from authoritative cart
 * data. Source-scoped format relationships require an exact owned variant, so
 * a Tub can recommend its matching Travel Pack and a small Refill Roll can
 * recommend its matching Tie-Handle format without translated-title inference.
 * The renderer shows up to three safe results. A short safe pool degrades to
 * one or two cards instead of disappearing; only a genuinely empty/unknown
 * pool stays hidden.
 *
 * @param {{
 *   candidates?: Array<{productId: string, variantId: string, rec: string, family: string, needs?: string, format?: string, sourceVariantIds?: string, available: boolean}>,
 *   cart?: {items?: Array<object>}|null,
 *   families?: Record<string, string>|null,
 *   formats?: Record<string, string>|null,
 * }} input
 * @returns {Array<object>}
 */
export function eligibleCartUpsellCandidates({
  candidates = [],
  cart = null,
  families = null,
  formats = {},
} = {}) {
  if (!Array.isArray(candidates) || !cart || !Array.isArray(cart.items) || !plainRecord(families) || !plainRecord(formats)) {
    return [];
  }
  const items = cart.items.map((item) => ({
    productId: String(item?.product_id ?? item?.productId ?? ''),
    variantId: String(item?.variant_id ?? item?.variantId ?? ''),
  }));
  // Without a stable family for every current cart line we cannot safely apply
  // family/needs exclusions, so preserve the initially hidden module.
  if (items.some((item) => !nonEmptyString(item.productId) || !nonEmptyString(item.variantId) || !nonEmptyString(families[item.productId]))) {
    return [];
  }

  const ownedVariants = new Set(items.map((item) => item.variantId));
  const ownedFamilies = new Set(items.map((item) => families[item.productId]));
  const ownedProducts = new Set(items.map((item) => item.productId));
  const ownedProductFormats = new Set(
    items
      .map((item) => {
        const metadata = formats[item.variantId];
        if (!plainRecord(metadata)) return '';
        const formatProductId = String(metadata.productId ?? '');
        const format = String(metadata.format ?? '');
        return formatProductId === item.productId && nonEmptyString(format)
          ? `${item.productId}|${format}`
          : '';
      })
      .filter(nonEmptyString),
  );
  const seenVariants = new Set();
  const seenProductFormats = new Set();
  const selected = [];

  const sourceIds = (candidate) => String(candidate?.sourceVariantIds ?? '')
    .split(',')
    .map((id) => id.trim())
    .filter(nonEmptyString);
  const rankedCandidates = candidates
    .map((candidate, index) => ({
      candidate,
      index,
      // An exact source→target relationship is more useful than a generic
      // family staple. Legacy unscoped format candidates remain the last tier.
      tier: candidate?.rec === 'format' && sourceIds(candidate).length
        ? 0
        : candidate?.rec === 'staple'
          ? 1
          : candidate?.rec === 'alternative'
            ? 2
            : 3,
    }))
    .sort((a, b) => (a.tier - b.tier) || (a.index - b.index))
    .map(({ candidate }) => candidate);

  for (const candidate of rankedCandidates) {
    const productId = String(candidate?.productId ?? '');
    const variantId = String(candidate?.variantId ?? '');
    const family = String(candidate?.family ?? '');
    const rec = String(candidate?.rec ?? 'staple');
    const format = String(candidate?.format ?? '');
    if (!candidate || candidate.available !== true || !nonEmptyString(productId) || !nonEmptyString(variantId) || !nonEmptyString(family)) continue;
    // Candidate family metadata must agree with the server-rendered catalogue map.
    if (families[productId] !== family || ownedVariants.has(variantId) || seenVariants.has(variantId)) continue;

    if (rec === 'format') {
      if (!nonEmptyString(format) || !ownedProducts.has(productId) || ownedProductFormats.has(`${productId}|${format}`)) continue;
      const requiredSources = sourceIds(candidate);
      if (requiredSources.length && !requiredSources.some((sourceId) => ownedVariants.has(sourceId))) continue;
    } else if (rec === 'alternative') {
      if (!ownedFamilies.has(family) || ownedProducts.has(productId)) continue;
    } else if (rec === 'staple') {
      if (ownedFamilies.has(family)) continue;
      const needs = String(candidate?.needs ?? '')
        .split(',')
        .map((need) => need.trim())
        .filter(Boolean);
      // Existing merchant semantics are an OR gate: a candidate with several
      // family keys may surface when any configured pairing is in the cart.
      if (needs.length && !needs.some((need) => ownedFamilies.has(need))) continue;
    } else {
      continue;
    }

    const productFormat = `${productId}|${format}`;
    if (seenProductFormats.has(productFormat)) continue;
    seenVariants.add(variantId);
    seenProductFormats.add(productFormat);
    selected.push(candidate);
  }
  return selected;
}

export function selectCartUpsellCandidates(input = {}) {
  const minimum = Number.isInteger(input.minimumDisplay) && input.minimumDisplay > 0
    ? input.minimumDisplay
    : 1;
  const requestedMaximum = Number.isInteger(input.maximumDisplay) && input.maximumDisplay > 0
    ? input.maximumDisplay
    : 3;
  const maximum = Math.max(minimum, requestedMaximum);
  const eligible = eligibleCartUpsellCandidates(input);
  return eligible.length >= minimum ? eligible.slice(0, maximum) : [];
}

export function lastVisibleCartUpsellCandidate(candidates = [], selected = []) {
  if (!Array.isArray(candidates) || !Array.isArray(selected)) return null;
  const selectedSet = new Set(selected);
  return candidates.filter((candidate) => selectedSet.has(candidate)).at(-1) || null;
}

class PogiCart extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    this.sectionId = this.dataset.sectionId;
    const freeShipCents = parseInt(this.dataset.freeShipCents, 10);
    this.freeShipCents = Number.isFinite(freeShipCents) ? freeShipCents : 0;
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.currency = this.dataset.currency || 'USD';
    this.cartAddUrl = this.dataset.cartAddUrl || '/cart/add.js';
    this.cartChangeUrl = this.dataset.cartChangeUrl || '/cart/change.js';
    this.cartJsonUrl = this.dataset.cartJsonUrl || '/cart.js';
    this.cartUrl = this.dataset.cartUrl || '/cart';
    this.desiredQuantities = this.desiredQuantities || new Map();
    this.mutationPending = Boolean(this.mutationPending);
    this.mutationBlocked = Boolean(this.mutationBlocked);
    this._busyControlStates = this._busyControlStates || new Map();
    this.refreshGeneration = 0;
    try { const _i = this.querySelector('[data-pogi-cart-i18n]'); if (_i) I18N = JSON.parse(_i.textContent); } catch (e) {}

    this.addEventListener('click', this.#onClick);

    this.refresh();
    if (this.mutationBlocked) this.#setBusy(true);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.refreshController?.abort();
    this._bound = false;
  }

  /* ------------------------------------------------------------------ events */

  #captureLineFocus(control, line) {
    const active = document.activeElement;
    if (!(control instanceof HTMLElement) || !(line instanceof HTMLElement)) return null;
    if (!(active instanceof HTMLElement) || !control.contains(active) && active !== control) return null;
    return {
      key: line.dataset.lineKey || '',
      index: Array.from(this.querySelectorAll('[data-line-key]')).indexOf(line),
      action: control.dataset.cartAction || '',
      plan: control.dataset.plan ?? null,
    };
  }

  #restoreLineFocus(snapshot) {
    if (!snapshot) return;
    const rows = Array.from(this.querySelectorAll('[data-line-key]'));
    const keyedRow = rows.find((row) => row instanceof HTMLElement && row.dataset.lineKey === snapshot.key);
    const fallbackIndex = Math.min(Math.max(snapshot.index, 0), rows.length - 1);
    const targetRow = keyedRow || rows[fallbackIndex];
    let target = null;

    if (targetRow instanceof HTMLElement) {
      const actionSelector = snapshot.action
        ? `[data-cart-action="${CSS.escape(snapshot.action)}"]`
        : '';
      if (actionSelector && snapshot.plan !== null) {
        const planTarget = targetRow.querySelector(`${actionSelector}[data-plan="${CSS.escape(snapshot.plan)}"]`);
        if (
          planTarget instanceof HTMLElement &&
          !planTarget.matches(':disabled, [hidden]') &&
          !planTarget.closest('[hidden]')
        ) {
          target = planTarget;
        }
      }
      if (!target && actionSelector) {
        const actionTarget = targetRow.querySelector(actionSelector);
        if (
          actionTarget instanceof HTMLElement &&
          !actionTarget.matches(':disabled, [hidden]') &&
          !actionTarget.closest('[hidden]')
        ) {
          target = actionTarget;
        }
      }
      target ||= targetRow.querySelector('button:not([disabled]):not([hidden]), a[href]:not([hidden])');
    }

    target ||= this.querySelector('.pogi-cart__empty-cta a');
    target ||= this.querySelector('.pogi-cart__h1');
    if (
      !(target instanceof HTMLElement) ||
      target.matches(':disabled, [hidden]') ||
      target.closest('[hidden]')
    ) return;
    if (!target.matches('a[href], button, input, select, textarea, [tabindex]')) target.tabIndex = -1;
    target.focus({ preventScroll: true });
  }

  #onClick = (e) => {
    const el = e.target.closest('[data-cart-action]');
    if (!el || !this.contains(el)) return;
    if (this.mutationPending || this.mutationBlocked) {
      e.preventDefault();
      return;
    }
    const action = el.dataset.cartAction;
    const line = el.closest('[data-line-key]');
    const focusSnapshot = this.#captureLineFocus(el, line);

    switch (action) {
      case 'inc':
      case 'dec': {
        const key = line.dataset.lineKey;
        const renderedQuantity = parseInt(line.dataset.qty, 10) || 1;
        const qty = this.desiredQuantities.has(key)
          ? this.desiredQuantities.get(key)
          : renderedQuantity;
        const next = action === 'inc' ? qty + 1 : Math.max(0, qty - 1);
        this.desiredQuantities.set(key, next);
        this.#change({ id: key, quantity: next }).finally(() => this.#restoreLineFocus(focusSnapshot));
        break;
      }
      case 'remove':
        this.#change({ id: line.dataset.lineKey, quantity: 0 }).finally(() => this.#restoreLineFocus(focusSnapshot));
        break;
      case 'save':
        this.#saveForLater(line).finally(() => this.#restoreLineFocus(focusSnapshot));
        break;
      case 'set-plan': {
        const plan = el.dataset.plan;
        this.#change({ id: line.dataset.lineKey, quantity: parseInt(line.dataset.qty, 10) || 1, selling_plan: plan === '' ? null : parseInt(plan, 10) })
          .finally(() => this.#restoreLineFocus(focusSnapshot));
        break;
      }
      case 'add': {
        const id = parseInt(el.dataset.variantId, 10);
        if (id) this.#add(id);
        break;
      }
    }
  };

  /* -------------------------------------------------------------- mutations */

  #enqueueMutation(task) {
    if (this.mutationPending || this.mutationBlocked) return Promise.resolve(null);
    this.mutationPending = true;
    this.#setBusy(true);
    return Promise.resolve()
      .then(() => (this.mutationBlocked ? null : task()))
      .finally(() => {
        this.mutationPending = false;
        if (!this.mutationBlocked) this.#setBusy(false);
      });
  }

  #change(body, beforeChange = null) {
    const key = String(body.id);
    return this.#enqueueMutation(() => this.#mutate(this.cartChangeUrl, body, beforeChange))
      .finally(() => {
        if (this.desiredQuantities.get(key) === body.quantity) {
          this.desiredQuantities.delete(key);
        }
      });
  }

  async #add(id, { quantity = 1, sellingPlanId = null, properties = {}, beforeAdd = null } = {}) {
    const item = {
      id,
      quantity: Math.max(1, parseInt(quantity, 10) || 1),
      properties: savedProperties(properties),
    };
    const plan = parseInt(sellingPlanId, 10);
    if (Number.isFinite(plan)) item.selling_plan = plan;
    return this.#enqueueMutation(async () => {
      this._lastMutationOutcome = null;
      if (beforeAdd) await beforeAdd(await this.#fetchCart());
      return this.#mutate(this.cartAddUrl, { items: [item] });
    });
  }

  // Single busy toggle for the cart-page loading state. Each network step has
  // its own abort deadline; keep controls locked until reconciliation finishes.
  #setBusy(on) {
    if (on) {
      this.classList.add('is-busy');
      this.setAttribute('aria-busy', 'true');
      for (const control of this.querySelectorAll('button, input, select')) {
        if (!this._busyControlStates.has(control)) {
          this._busyControlStates.set(control, control.disabled === true);
        }
        control.disabled = true;
      }
    } else {
      this.classList.remove('is-busy');
      this.removeAttribute('aria-busy');
      for (const [control, wasDisabled] of this._busyControlStates) {
        if (control.isConnected) control.disabled = wasDisabled;
      }
      this._busyControlStates.clear();
    }
  }

  async #mutate(url, body, beforeChange = null) {
    this.#clearError();
    this._lastMutationOutcome = null;
    const isAdd = url.endsWith('/add.js');
    const quantity = isAdd
      ? Number(body.items?.[0]?.quantity) || 1
      : Math.max(0, Number(body.quantity) || 0);
    const action = isAdd ? 'add' : quantity === 0 ? 'remove' : 'update';
    const lines = isAdd
      ? body.items.map((item) => ({
          merchandiseId: String(item.id),
          quantity: Number(item.quantity) || 1,
        }))
      : [{ id: String(body.id), quantity }];
    const cartUpdate = beginPogiCartUpdate({
      target: this,
      action,
      context: 'cart',
      lines,
      source: 'pogi-cart',
      detail: {
        sellingPlanId: body.selling_plan ?? body.items?.[0]?.selling_plan ?? null,
      },
    });

    try {
      if (isAdd) {
        const result = await addPogiCartLines({
          cartAddUrl: this.cartAddUrl,
          cartJsonUrl: this.cartJsonUrl,
          items: body.items,
        });
        const viewRecovered = await this.#renderCommittedCart(result.cart);
        cartUpdate.resolve(result.cart, {
          recovered: result.recovered || viewRecovered,
          commitState: result.commitState,
          suppressAutoOpen: true,
          skipSectionRefresh: true,
        });
        this._lastMutationOutcome = { commitState: 'committed', retrySafe: false };
        return result.cart;
      }

      const result = await withPogiCartMutationLock(() => this.#mutateLineData(url, body, beforeChange));
      const viewRecovered = await this.#renderCommittedCart(result.cart);
      cartUpdate.resolve(result.cart, {
        sections: result.cart?.sections,
        recovered: result.recovered || viewRecovered,
        commitState: result.commitState,
        suppressAutoOpen: true,
        skipSectionRefresh: true,
      });
      this._lastMutationOutcome = { commitState: 'committed', retrySafe: false };
      return result.cart;
    } catch (err) {
      const currentCart = err.currentCart || null;
      const resolvedCommitState = err.commitState || 'unknown';
      const retrySafe = err.retrySafe ?? resolvedCommitState === 'rejected';
      this._lastMutationOutcome = { commitState: resolvedCommitState, retrySafe };

      // The Shopify mutation can be authoritative even when the follow-up
      // section refresh fails (for example, a throttled sections request).
      // Do not tell customers that a confirmed add failed: settle the standard
      // event as committed, lock the stale view, then reload the cart once.
      if (resolvedCommitState === 'committed' && currentCart) {
        console.warn('[pogi-cart] cart committed; recovering stale cart view', err);
        cartUpdate.resolve(currentCart, {
          recovered: true,
          commitState: 'committed',
          suppressAutoOpen: true,
          skipSectionRefresh: true,
          viewRecoveryFailed: true,
        });
        this.#scheduleAuthoritativeNavigation();
        return currentCart;
      }

      console.error('[pogi-cart]', err);
      this.#showError(err);
      cartUpdate.fail(err, currentCart, {
        didError: true,
        recovered: resolvedCommitState === 'committed',
        commitState: resolvedCommitState,
        retrySafe,
        suppressAutoOpen: true,
        skipSectionRefresh: true,
      });
      if (!retrySafe) this.#scheduleAuthoritativeNavigation();
      return null;
    }
  }

  async #saveForLater(line) {
    if (!line) return Promise.resolve(null);
    const titleEl = line.querySelector('.pogi-cart__line-title');
    const variantEl = line.querySelector('.pogi-cart__line-variant');
    const img = line.querySelector('.pogi-cart__thumb img');
    const display = {
      title: titleEl ? titleEl.textContent.trim() : '',
      variant: variantEl ? variantEl.textContent.trim() : '',
      img: img ? img.getAttribute('src') : '',
      url: titleEl ? titleEl.getAttribute('href') : '',
    };
    let transactionId = null;
    const cart = await this.#change({ id: line.dataset.lineKey, quantity: 0 }, (beforeCart) => {
      const prepared = prepareSavedLine(beforeCart, line.dataset.lineKey, display);
      transactionId = prepared.transactionId;
      writeSavedItems(prepared.saved);
      this.#renderSaved();
    });
    if (!cart && transactionId && this._lastMutationOutcome?.retrySafe) {
      this.#rollbackSavedChange(transactionId);
    }
    if (cart) this.#reconcileSaved(cart);
    this.#renderSaved();
    return cart;
  }

  /* ---------------------------------------------------------------- network */

  async #mutateLineData(url, body, beforeChange = null) {
    let beforeCart;
    try {
      beforeCart = await this.#fetchCart();
    } catch (error) {
      error.commitState = 'preflight';
      error.retrySafe = true;
      throw error;
    }

    try {
      beforeChange?.(beforeCart);
    } catch (error) {
      error.currentCart = beforeCart;
      error.commitState = 'preflight';
      error.retrySafe = true;
      throw error;
    }

    try {
      const cart = await this.#post(url, body);
      return { cart, recovered: false, commitState: 'committed' };
    } catch (error) {
      let currentCart = error.currentCart || null;
      try {
        if (!currentCart) currentCart = await this.#fetchCart();
      } catch (_) {
        // Preserve the mutation failure when authoritative recovery is unavailable.
      }
      if (
        error.commitState !== 'rejected' &&
        currentCart &&
        cartReflectsLineChange(beforeCart, currentCart, body)
      ) {
        return { cart: currentCart, recovered: true, commitState: 'committed' };
      }
      error.currentCart = currentCart;
      error.commitState = error.commitState || 'unknown';
      error.retrySafe = error.retrySafe ?? error.commitState === 'rejected';
      throw error;
    }
  }

  async #renderCommittedCart(cart) {
    const html = cart?.sections?.[this.sectionId];
    if (typeof html !== 'string' || html.trim() === '') {
      try {
        await this.#refreshSection();
        return true;
      } catch (recoveryError) {
        recoveryError.currentCart = cart;
        recoveryError.commitState = 'committed';
        recoveryError.retrySafe = false;
        throw recoveryError;
      }
    }
    try {
      await this.#applySections(cart);
      return false;
    } catch (renderError) {
      try {
        await this.#refreshSection();
        return true;
      } catch (recoveryError) {
        recoveryError.cause = renderError;
        recoveryError.currentCart = cart;
        recoveryError.commitState = 'committed';
        recoveryError.retrySafe = false;
        throw recoveryError;
      }
    }
  }

  #scheduleAuthoritativeNavigation() {
    this.mutationBlocked = true;
    this.dataset.sectionRecoveryFailed = 'true';
    this.#setBusy(true);
    if (this._authoritativeNavigationScheduled) return;
    this._authoritativeNavigationScheduled = true;
    window.setTimeout(() => window.location.assign(this.cartUrl), 2500);
  }

  #sectionParams() {
    return { sections: this.sectionId, sections_url: window.location.pathname };
  }

  async #fetchCart() {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), CART_REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(this.cartJsonUrl, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        credentials: 'same-origin',
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Cart refresh failed (${response.status})`);
      return assertPogiAjaxCart(await response.json());
    } finally {
      window.clearTimeout(timeout);
    }
  }

  async #post(url, body) {
    const payload = (body && body.sections) ? body : { ...body, ...this.#sectionParams() };
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), CART_REQUEST_TIMEOUT_MS);
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        signal: controller.signal,
        body: JSON.stringify(payload),
      });
      const text = await res.text();
      let data = null;
      try { data = JSON.parse(text); } catch (_) { /* noop */ }
      if (!res.ok) {
        console.error('[pogi-cart] request failed', data || text);
        const error = new Error(data?.description || data?.message || `Cart request failed (${res.status})`);
        error.response = data;
        error.commitState = 'rejected';
        error.retrySafe = true;
        throw error;
      }
      return assertPogiAjaxCart(data);
    } finally {
      window.clearTimeout(timeout);
    }
  }

  async #refreshSection() {
    const url = new URL(window.location.href);
    url.searchParams.set('sections', this.sectionId);
    url.hash = '';
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), CART_REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        credentials: 'same-origin',
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Cart section refresh failed (${response.status})`);
      const sections = await response.json();
      const html = sections?.[this.sectionId];
      if (!html) throw new Error('Cart section refresh returned no HTML');
      await morphSection(this.sectionId, html, { mode: 'full' });
      this.refresh();
    } finally {
      window.clearTimeout(timeout);
    }
  }

  async #applySections(cart) {
    const html = cart.sections && cart.sections[this.sectionId];
    if (html) await morphSection(this.sectionId, html, { mode: 'full' });
    // Morph keeps THIS element, so connectedCallback won't fire again — rerun the
    // client-only bits against the fresh (now-swapped) DOM.
    this.refresh(cart);
  }

  #clearError() {
    const status = this.querySelector('[data-pogi-cart-error]');
    if (!status) return;
    status.hidden = true;
    status.textContent = '';
  }

  #showError(error) {
    const status = this.querySelector('[data-pogi-cart-error]');
    if (!status) return;
    status.textContent =
      error?.response?.description ||
      this.dataset.updateError ||
      'Unable to update your cart. Please try again.';
    status.hidden = false;
  }

  /* ----------------------------------------------------- client re-renders */

  async refresh(cartSnapshot = null) {
    // Section morphs replace controls while the mutation is still settling.
    if (this.mutationPending || this.mutationBlocked) this.#setBusy(true);
    const generation = ++this.refreshGeneration;
    this.refreshController?.abort();
    const controller = new AbortController();
    this.refreshController = controller;
    let cart = cartSnapshot;
    if (!cart) {
      const timeout = window.setTimeout(
        () => controller.abort(),
        CART_REQUEST_TIMEOUT_MS,
      );
      try {
        const res = await fetch(this.cartJsonUrl, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
          credentials: 'same-origin',
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`Cart refresh failed (${res.status})`);
        cart = await res.json();
      } catch (error) {
        if (error?.name !== 'AbortError') console.warn('[pogi-cart] client refresh failed', error);
      } finally {
        window.clearTimeout(timeout);
      }
    }
    if (generation !== this.refreshGeneration) return;
    this.#renderCrossSell(cart);
    this.#renderGapCloser(cart);
    this.#reconcileSaved(cart);
    this.#renderSaved();
    if (this.mutationPending || this.mutationBlocked) this.#setBusy(true);
  }

  #renderCrossSell(cart) {
    const wrap = this.querySelector('[data-pogi-xsell]');
    const grid = this.querySelector('[data-pogi-xsell-grid]');
    if (!wrap || !grid) return;
    const tiles = Array.from(grid.querySelectorAll('[data-xtile]'));
    if (!tiles.length) { wrap.hidden = true; return; }

    let families = null;
    let formats = null;
    try { families = JSON.parse(grid.dataset.families || 'null'); } catch (_) { /* fail closed below */ }
    try { formats = JSON.parse(grid.dataset.formats || 'null'); } catch (_) { /* fail closed below */ }
    const candidates = tiles.map((tile) => ({
      element: tile,
      productId: tile.dataset.productId,
      variantId: tile.dataset.variantId,
      rec: tile.dataset.rec,
      family: tile.dataset.family,
      needs: tile.dataset.needs,
      format: tile.dataset.format,
      sourceVariantIds: tile.dataset.sourceVariantIds,
      available: tile.dataset.available === 'true',
    }));
    const selectorInput = {
      candidates,
      cart,
      families,
      formats,
      minimumDisplay: 1,
      maximumDisplay: 3,
    };
    const eligible = eligibleCartUpsellCandidates(selectorInput);
    const show = selectCartUpsellCandidates(selectorInput);
    const shownTiles = new Set(show.map((candidate) => candidate.element));
    const lastVisible = show.length % 2 === 1
      ? lastVisibleCartUpsellCandidate(candidates, show)?.element || null
      : null;
    tiles.forEach((tile) => {
      tile.hidden = !shownTiles.has(tile);
      tile.classList.toggle('pogi-cart__xtile--last', lastVisible === tile);
    });
    grid.dataset.visibleCount = String(show.length);
    wrap.hidden = show.length === 0;
    this._eligibleRecs = eligible.map((candidate) => candidate.element);
    const hasUnmappedLine = Array.isArray(cart?.items) && cart.items.some(
      (item) => !nonEmptyString(families?.[String(item?.product_id ?? item?.productId ?? '')]),
    );
    if (hasUnmappedLine && !this._warnedUnmappedUpsell) {
      console.warn('[pogi-cart] upsell hidden: cart product lacks configured family metadata');
      this._warnedUnmappedUpsell = true;
    }
  }

  #renderGapCloser(cart) {
    const host = this.querySelector('[data-pogi-gap]');
    if (!host) return;
    const total = cart ? cart.total_price : null;
    if (total == null) { host.hidden = true; return; }
    const gap = this.freeShipCents - total;
    if (gap <= 0) { host.hidden = true; return; }

    const recs = (this._eligibleRecs || []).slice();
    if (!recs.length) { host.hidden = true; return; }
    const withPrice = recs.map((t) => ({ t, price: parseInt(t.dataset.price, 10) || 0 }));
    const crossers = withPrice.filter((r) => r.price >= gap).sort((a, b) => a.price - b.price);
    const pick = crossers[0] || withPrice.slice().sort((a, b) => b.price - a.price)[0];
    if (!pick) { host.hidden = true; return; }

    const title = pick.t.dataset.title || pick.t.querySelector('.pogi-cart__xtile-title')?.textContent || 'this';
    host.innerHTML = '';
    const label = document.createElement('span');
    label.innerHTML = tpl('addUnlock', 'Add <strong>[[title]]</strong> &middot; [[price]] to&nbsp;unlock&nbsp;it', { title: this.#esc(title), price: this.money(pick.price) });
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute('data-cart-action', 'add');
    btn.setAttribute('data-variant-id', pick.t.dataset.variantId);
    btn.textContent = tpl('xsellAdd', 'Add');
    host.appendChild(label);
    host.appendChild(btn);
    host.hidden = false;
  }

  #renderSaved() {
    const host = this.querySelector('[data-pogi-saved]');
    if (!host) return;
    const saved = this.#readSaved();
    if (!saved.length) { host.hidden = true; host.innerHTML = ''; return; }
    const rows = saved.map((s) => `
      <div class="pogi-cart__saved-row" data-saved-id="${this.#esc(this.#savedIdentity(s))}">
        <a class="m" href="${this.#esc(this.#savedUrl(s))}">${s.img ? `<img src="${this.#esc(s.img)}" alt="">` : ''}</a>
        <div class="info">
          <div class="t"><a href="${this.#esc(this.#savedUrl(s))}" style="color:inherit;text-decoration:none">${this.#esc(s.title)}</a></div>
          ${s.variant ? `<div class="v">${this.#esc(s.variant)}</div>` : ''}
        </div>
        <button type="button" class="pogi-cart__link" style="color:var(--pogi-olive);font-weight:700" data-saved-move="${this.#esc(this.#savedIdentity(s))}"${s.pendingCartChange ? ' disabled' : ''}>${tpl('moveToCart', 'Move to cart')}</button>
        <button type="button" class="pogi-cart__link" data-saved-remove="${this.#esc(this.#savedIdentity(s))}" aria-label="${tpl('removeSaved', 'Remove saved item')}"${s.pendingCartChange ? ' disabled' : ''}>&times;</button>
      </div>`).join('');
    host.innerHTML = `<div class="pogi-cart__saved-h">${this.#esc(tpl('savedForLater', 'Saved for later'))} &middot; ${saved.length}</div>${rows}`;
    host.hidden = false;
    if (window.location.hash === '#pogi-saved-items' && !this._savedAnchorHandled) {
      this._savedAnchorHandled = true;
      host.tabIndex = -1;
      host.focus({ preventScroll: true });
      host.scrollIntoView({ block: 'start' });
    }

    host.querySelectorAll('[data-saved-move]').forEach((b) => b.addEventListener('click', async () => {
      const savedId = b.getAttribute('data-saved-move');
      const savedRows = Array.from(host.querySelectorAll('[data-saved-id]'));
      const savedIndex = savedRows.indexOf(b.closest('[data-saved-id]'));
      const current = this.#readSaved();
      const entry = current.find((s) => this.#savedIdentity(s) === savedId);
      if (!entry || entry.pendingCartChange) return;
      let transactionId = null;
      try {
        const result = await this.#add(parseInt(entry.variantId, 10), {
          quantity: entry.quantity,
          sellingPlanId: entry.sellingPlanId,
          properties: entry.properties,
          beforeAdd: (beforeCart) => {
            const prepared = prepareSavedMove(beforeCart, entry);
            transactionId = prepared.transactionId;
            writeSavedItems(prepared.saved);
            this.#renderSaved();
          },
        });
        if (!result && transactionId && this._lastMutationOutcome?.retrySafe) {
          this.#rollbackSavedChange(transactionId);
        }
        if (result) this.#reconcileSaved(result);
      } catch (error) {
        if (transactionId) this.#rollbackSavedChange(transactionId);
        this.#showError(error);
      }
      this.#renderSaved();
      this.#restoreSavedFocus(savedIndex, 'move');
    }));
    host.querySelectorAll('[data-saved-remove]').forEach((b) => b.addEventListener('click', () => {
      const savedId = b.getAttribute('data-saved-remove');
      const savedRows = Array.from(host.querySelectorAll('[data-saved-id]'));
      const savedIndex = savedRows.indexOf(b.closest('[data-saved-id]'));
      try {
        const saved = this.#readSaved(true);
        if (saved.find((s) => this.#savedIdentity(s) === savedId)?.pendingCartChange) return;
        this.#writeSaved(saved.filter((s) => this.#savedIdentity(s) !== savedId));
      } catch (error) {
        this.#showError(error);
        return;
      }
      this.#renderSaved();
      this.#restoreSavedFocus(savedIndex, 'remove');
    }));
  }

  #restoreSavedFocus(index, action) {
    const rows = Array.from(this.querySelectorAll('[data-saved-id]'));
    const targetRow = rows[Math.min(Math.max(index, 0), rows.length - 1)];
    let target = targetRow instanceof HTMLElement
      ? targetRow.querySelector(action === 'move' ? '[data-saved-move]' : '[data-saved-remove]')
      : null;
    target ||= this.querySelector('.pogi-cart__empty-cta a');
    target ||= this.querySelector('.pogi-cart__h1');
    if (
      !(target instanceof HTMLElement) ||
      target.matches(':disabled, [hidden]') ||
      target.closest('[hidden]')
    ) return;
    if (!target.matches('a[href], button, [tabindex]')) target.tabIndex = -1;
    target.focus({ preventScroll: true });
  }

  #savedIdentity(entry) { return savedIdentity(entry); }

  #savedUrl(entry) {
    try {
      const url = new URL(entry.url || '', window.location.origin);
      const productPath = url.pathname.match(/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/([^/]+)\/?$/i);
      if (url.origin !== window.location.origin || !productPath) return this.cartUrl;
      const root = new URL(this.cartUrl, window.location.origin).pathname.replace(/\/cart\/?$/, '');
      url.pathname = `${root}/products/${productPath[1]}`;
      url.searchParams.set('variant', String(entry.variantId));
      if (Number(entry.sellingPlanId) > 0) url.searchParams.set('selling_plan', String(entry.sellingPlanId));
      else url.searchParams.delete('selling_plan');
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (_) {
      return this.cartUrl;
    }
  }

  #reconcileSaved(cart) {
    try { reconcileSavedItems(cart); } catch (error) { this.#showError(error); }
  }

  #rollbackSavedChange(transactionId) {
    try { rollbackSavedChange(transactionId); } catch (error) { this.#showError(error); }
  }

  /* ------------------------------------------------------------- utilities */

  #readSaved(strict = false) { return readSavedItems(strict); }
  #writeSaved(list) { writeSavedItems(list); }

  #esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  money(cents) {
    return formatShopifyMoney(Number(cents) || 0, this.moneyFormat, this.currency);
  }
}

if (!customElements.get('pogi-cart')) {
  customElements.define('pogi-cart', PogiCart);
}
